import { useEffect, useState } from "react";
import { sitesApi } from "../../services/sitesApi";
import { Field } from "./Field";

/**
 * Choose a site by name.
 *
 * The sibling of HivePicker (#211), added for #212 because assigning a
 * checklist to a whole yard needs the same treatment the hive half already
 * had: the user picks "North Orchard", and site_id never appears on screen.
 *
 * Same reasoning as HivePicker throughout, including why this is not built
 * on SelectOrAdd -- a site is a real record, and "+ Add new" inside an
 * unrelated form would mean creating one by accident.
 *
 * The option text is `dock_name`. That is what the column is called and
 * has always been called; reaching for `name` renders a list of blank
 * options that looks exactly like a list that failed to load.
 */
export function SitePicker({ label = "Site", value, onChange, sites: provided, required = false, includeBlank = true }) {
  const [sites, setSites] = useState(provided || []);
  const [loading, setLoading] = useState(!provided);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (provided) {
      setSites(provided);
      return;
    }
    let cancelled = false;
    sitesApi
      .list()
      .then((r) => {
        if (cancelled) return;
        setSites(r.sites || []);
      })
      // Distinct from "you have no sites", which would read as data loss.
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [provided]);

  const hint = failed
    ? "Couldn't load your sites. Refresh and try again."
    : !loading && sites.length === 0
      ? "No sites yet."
      : undefined;

  return (
    <Field label={label} required={required} hint={hint}>
      {(id, aria) => (
        <select
          id={id}
          {...aria}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          disabled={loading || failed}
          className="touch-target"
        >
          {includeBlank ? <option value="">{loading ? "Loading..." : "Select a site..."}</option> : null}
          {sites.map((s) => (
            <option key={s.site_id} value={s.site_id}>
              {/* dock_name, NOT name. app.Sites has never had a `name`
                  column -- V002 called it dock_name and nothing has
                  renamed it since -- so this rendered an option per site
                  with no text in it. The control looked like it had
                  loaded nothing while actually holding every site the
                  account owns.

                  Reported on the harvest form; it has been this way in
                  Inspection Checklists since #212, where the same blank
                  list sat under "Which site". */}
              {s.dock_name || s.site_name || `Site ${s.site_id}`}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}
